import type {
  CsspBrand,
  CsspFilters,
  CsspOfferGroup,
  CsspOffersResult,
} from "./types";
import type { Product } from "@/types/product";

export const CSSP_SEARCH_CACHE_TTL_MS = 10 * 60 * 1000;

export type CsspSearchCacheContext = {
  city: string;
  cityId?: string;
  cityCode?: string;
  userFingerprint: string;
  sessionKey?: string;
};

export type CsspSearchUiState = {
  skuInput: string;
  useAnalogs: boolean;
  brands: CsspBrand[];
  selectedBrand: CsspBrand;
  filters: CsspFilters;
  expandedSections: CsspOfferGroup[];
  expandedGroups: string[];
  isFiltersOpen: boolean;
  csspStockProducts: Product[];
  scrollTop: number;
};

export type CsspCachedSearch = {
  artikul: string;
  brand: string;
  useAnalogs: boolean;
  context: CsspSearchCacheContext;
  completedAt: number;
  offersResult: CsspOffersResult;
  ui: CsspSearchUiState;
};

type CsspSearchLookup = Pick<CsspCachedSearch, "artikul" | "brand" | "useAnalogs" | "context">;

let cachedSearch: CsspCachedSearch | null = null;
let expirationTimer: ReturnType<typeof setTimeout> | null = null;

const normalize = (value: string) => value.trim().toLocaleLowerCase("ru-RU");

const isSameLookup = (left: CsspSearchLookup, right: CsspSearchLookup) =>
  normalize(left.artikul) === normalize(right.artikul) &&
  normalize(left.brand) === normalize(right.brand) &&
  left.useAnalogs === right.useAnalogs &&
  normalize(left.context.city) === normalize(right.context.city) &&
  (left.context.cityId ?? "") === (right.context.cityId ?? "") &&
  (left.context.cityCode ?? "") === (right.context.cityCode ?? "") &&
  (left.context.sessionKey ?? "") === (right.context.sessionKey ?? "") &&
  left.context.userFingerprint === right.context.userFingerprint;

const clearExpirationTimer = () => {
  if (expirationTimer === null) return;
  clearTimeout(expirationTimer);
  expirationTimer = null;
};

const scheduleExpiration = () => {
  clearExpirationTimer();
  if (!cachedSearch) return;

  const remaining = Math.max(cachedSearch.completedAt + CSSP_SEARCH_CACHE_TTL_MS - Date.now(), 0);
  expirationTimer = setTimeout(() => {
    csspSearchCache.clearExpired();
  }, remaining);
};

const cloneUiState = (ui: CsspSearchUiState): CsspSearchUiState => ({
  ...ui,
  brands: [...ui.brands],
  selectedBrand: { ...ui.selectedBrand },
  filters: {
    ...ui.filters,
    groups: [...ui.filters.groups],
    brands: [...ui.filters.brands],
  },
  expandedSections: [...ui.expandedSections],
  expandedGroups: [...ui.expandedGroups],
  csspStockProducts: [...ui.csspStockProducts],
});

/**
 * One in-memory result for the current tab. The cache deliberately has no
 * persistent backing: supplier responses and their customer prices must not
 * survive a tab reload, and dropping this reference makes the response
 * eligible for garbage collection.
 */
export const csspSearchCache = {
  replace(search: CsspCachedSearch) {
    cachedSearch = {
      ...search,
      context: { ...search.context },
      ui: cloneUiState(search.ui),
    };
    scheduleExpiration();
  },

  get(lookup: CsspSearchLookup, now = Date.now()): CsspCachedSearch | null {
    this.clearExpired(now);
    if (!cachedSearch || !isSameLookup(cachedSearch, lookup)) return null;

    return {
      ...cachedSearch,
      context: { ...cachedSearch.context },
      ui: cloneUiState(cachedSearch.ui),
    };
  },

  updateUi(lookup: CsspSearchLookup, update: (ui: CsspSearchUiState) => CsspSearchUiState) {
    if (!cachedSearch || !isSameLookup(cachedSearch, lookup)) return;
    cachedSearch = { ...cachedSearch, ui: cloneUiState(update(cachedSearch.ui)) };
  },

  updateScroll(lookup: CsspSearchLookup, scrollTop: number) {
    if (!cachedSearch || !isSameLookup(cachedSearch, lookup)) return;
    // Scrolling must not copy product data; this path runs frequently on large
    // result sets and only the number itself changed.
    cachedSearch = { ...cachedSearch, ui: { ...cachedSearch.ui, scrollTop } };
  },

  clear() {
    clearExpirationTimer();
    cachedSearch = null;
  },

  clearExpired(now = Date.now()) {
    if (cachedSearch && now - cachedSearch.completedAt >= CSSP_SEARCH_CACHE_TTL_MS) {
      this.clear();
    }
  },

  clearIfContextChanged(context: CsspSearchCacheContext) {
    if (!cachedSearch) return;
    if (
      normalize(cachedSearch.context.city) !== normalize(context.city) ||
      (cachedSearch.context.cityId ?? "") !== (context.cityId ?? "") ||
      (cachedSearch.context.cityCode ?? "") !== (context.cityCode ?? "") ||
      (cachedSearch.context.sessionKey ?? "") !== (context.sessionKey ?? "") ||
      cachedSearch.context.userFingerprint !== context.userFingerprint
    ) {
      this.clear();
    }
  },
};
